/**
 * Client entity factory.
 */
import type { PersonRef, RawRecord, RiskLevel } from './raw'

export type ClientTier = 'starter' | 'growth' | 'enterprise'

export interface Client {
  id: string | number | null
  name: string
  logo: string | null
  initials: string | null
  country: string
  industry: string
  contact: string
  primaryContactName: string
  primaryContactEmail: string
  trademarks: number
  coverage: number
  activeCases: number
  /** Null when the firm has not assessed this client — not the same as low. */
  risk: RiskLevel | null
  tier: ClientTier | string
  jurisCodes: string[]
  lawyer: PersonRef | null
  /** Every attached manager, not just the one the row has space for. */
  lawyers: { id: number; display_name: string }[]
  /** 'full' | 'partial' | 'paused' | 'none' — the server-side rollup. */
  monitoringStatus: string
  createdAt: string | null
  updatedAt: string | null
  selected: boolean
  opacity: number
  raw: RawRecord
}

export function createClient(raw: RawRecord = {}): Client {
  return {
    id: raw.id ?? null,
    name: raw.name ?? '',
    logo: raw.logo ?? null,
    initials: raw.initials ?? null,
    country: raw.country ?? '',
    industry: raw.industry ?? '',
    contact: raw.contact ?? '',
    primaryContactName: raw.primaryContactName ?? '',
    primaryContactEmail: raw.primaryContactEmail ?? '',
    trademarks: raw.trademarks ?? 0,
    coverage: raw.coverage ?? 0,
    activeCases: raw.activeCases ?? 0,
    risk: raw.risk ?? null,
    tier: raw.tier ?? 'starter', // starter | growth | enterprise
    jurisCodes: Array.isArray(raw.jurisCodes) ? raw.jurisCodes : [],
    lawyer: raw.lawyer ?? null, // { name, initials, style, muted }
    lawyers: Array.isArray(raw.lawyers) ? raw.lawyers : [],
    monitoringStatus: raw.monitoringStatus ?? '',
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
    selected: raw.selected ?? false,
    opacity: raw.opacity ?? 1,
    raw,
  }
}

export function createClientList(rawList: RawRecord[] = []): Client[] {
  return rawList.map(createClient)
}
